import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { ApiService } from '../../lib/api.service';
import { LogService } from '../../lib/log.service';
import { ModalService } from '../../lib/modal.service';
import { Organization } from '../../models/organization';
import { UserService } from '../../lib/user.service';
import { TranslateService } from '@ngx-translate/core';
import { config } from '../../../environments/all';
import { MultiAutoItem } from '../../models/multiautoitem';

@Component({
    selector: 'app-user-create',
    templateUrl: './user-create.component.html',
    styleUrls: ['./user-create.component.scss'],
    standalone: false,
})
export class UserCreateComponent implements OnInit {
    userCreateForm: FormGroup;
    organizationDataForm: FormGroup;
    isLoading = true;
    organizations: Organization[];
    multiSelOrgs: Array<MultiAutoItem> = [];
    organizationIds = [];
    roles = [
        { id: 1, name: 'User' },
        { id: 2, name: 'Professional' },
        { id: 3, name: 'Executive' },
        { id: 4, name: 'Executive Professional' },
        { id: 5, name: 'Joint Professional' },
        { id: 6, name: 'Relationship Manager' },
        { id: 7, name: 'Admin' },
    ];
    timezones: any;
    passwordType: string = 'password';

    constructor(
        private formBuilder: FormBuilder,
        private api: ApiService,
        private log: LogService,
        private modalService: ModalService,
        private translate: TranslateService,
        private userService: UserService,
        private router: Router,
        private location: Location
    ) {
        this.timezones = config.timezones;
    }

    ngOnInit() {
        this.initForms();
        this.loadOrganizations();
    }

    initForms() {
        this.userCreateForm = this.formBuilder.group({
            fullName: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.pattern(/^([\w+-.%]+@[\w-.]+\.[A-Za-z]{2,4},?)+$/)]],
            status: ['approved'],
            notificationOn: [true],
            timezone: [''],
            roleId: [1],
            lockedRole: [false],
            password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(32)]],
            orgId: [null],
        });

        this.organizationDataForm = this.formBuilder.group({
            orgId: [[]],
        });
    }

    loadOrganizations() {
        this.api.get('admin/organizations').subscribe(
            (results: any) => {
                this.organizations = Organization.initializeArray(results.data);
                this.multiSelOrgs = [];
                this.organizations.forEach(org => {
                    this.multiSelOrgs.push(new MultiAutoItem(org.id, org.name));
                });
                this.isLoading = false;
            },
            (error: any) => {
                this.log.error('Error loading organizations. ' + error.message);
                this.isLoading = false;
            }
        );
    }

    get f() {
        return this.userCreateForm.controls;
    }

    onSave() {
        if (this.userCreateForm.invalid) {
            this.userCreateForm.markAllAsTouched();
            return;
        }

        const roleId = this.userCreateForm.value.roleId;
        this.api
            .post('admin/usercreate', {
                Name: this.userCreateForm.value.fullName,
                Email: this.userCreateForm.value.email,
                RoleID: roleId,
                Status: this.userCreateForm.value.status,
                Password: this.userCreateForm.value.password,
                OrgID: roleId === 6
                    ? this.organizationDataForm.value.orgId
                    : this.userCreateForm.value.orgId,
                NotificationOn: this.userCreateForm.value.notificationOn,
                lockedRole: this.userCreateForm.value.lockedRole,
                Timezone: this.userCreateForm.value.timezone,
            })
            .subscribe(
                (data: any) => {
                    this.modalService.showAlert('Success', 'User was successfully created');
                    this.router.navigate(['/admin/user-listing']);
                },
                (error: any) => {
                    this.modalService.showAlert('Error', error.message);
                }
            );
    }

    onCancel() {
        this.location.back();
    }

    onRoleChange() {
        const roleId = this.userCreateForm.value.roleId;
        if (roleId === 6) {
            this.userCreateForm.get('orgId').clearValidators();
            this.userCreateForm.get('orgId').updateValueAndValidity();
            this.organizationDataForm.get('orgId').setValidators([Validators.required]);
            this.organizationDataForm.get('orgId').updateValueAndValidity();
        } else if (roleId === 7) {
            this.userCreateForm.get('orgId').clearValidators();
            this.userCreateForm.get('orgId').updateValueAndValidity();
            this.organizationDataForm.get('orgId').clearValidators();
            this.organizationDataForm.get('orgId').updateValueAndValidity();
        } else {
            this.userCreateForm.get('orgId').setValidators([Validators.required]);
            this.userCreateForm.get('orgId').updateValueAndValidity();
            this.organizationDataForm.get('orgId').clearValidators();
            this.organizationDataForm.get('orgId').updateValueAndValidity();
        }
    }

    onSelectOrgs(items: MultiAutoItem[]) {
        this.organizationIds = items.map(item => item.id);
        this.organizationDataForm.patchValue({
            orgId: this.organizationIds,
        });
    }

    onClickPassword() {
        this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
    }
}
